import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Dialog } from '@material-ui/core';

import { useConnectStore } from '../../../../../../store/models';
import CheckoutDrawer from '../../../../../../components/CheckoutDrawer';
import useCheckoutState from '../../../../../../components/CheckoutDrawer/useCheckoutState';
import { getAddOns } from '../../../../../../conf/chargify';
import { useStyles } from './purchaseTransactionsDialog.styles';
import usePurchaseTransactionsDialogState from './usePurchaseTransactionsDialogState';
import PickStep from './steps/PickStep';

const TITLE_ID = 'purchase-transactions-dialog-title';

function PurchaseTransactionsDialog ({ open, onClose, usage, onPaymentSuccess }) {
    const classes = useStyles();
    const connectStore = useConnectStore();

    const {
        packs,
        setPacks,
        incPacks,
        decPacks,
        packSize,
        pricePerPack,
        quantity,
        amount,
        plan,
        addOnValues,
    } = usePurchaseTransactionsDialogState();

    const { checkoutOpen, selectedPlan, selectedAddOnValues, handleChoosePlan, closeCheckout } = useCheckoutState();

    const { used, total } = usage;
    const remaining = total - used;
    const usedPercent = total > 0 ? Math.min((used / total) * 100, 100) : 0;

    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                aria-labelledby={TITLE_ID}
                PaperProps={{ className: classes.paper }}
                BackdropProps={{
                    style: {
                        backgroundColor: 'rgba(10,14,40,0.45)',
                        backdropFilter: 'blur(3px)',
                    },
                }}
            >
                <PickStep
                    titleId={TITLE_ID}
                    onClose={onClose}
                    used={used}
                    total={total}
                    remaining={remaining}
                    usedPercent={usedPercent}
                    packSize={packSize}
                    pricePerPack={pricePerPack}
                    packs={packs}
                    quantity={quantity}
                    amount={amount}
                    setPacks={setPacks}
                    incPacks={incPacks}
                    decPacks={decPacks}
                    goToPayment={() => handleChoosePlan(plan, addOnValues)}
                />
            </Dialog>

            <CheckoutDrawer
                open={checkoutOpen}
                plan={selectedPlan}
                addOnValues={selectedAddOnValues}
                onClose={closeCheckout}
                subscribeAction={(token) => connectStore.upgradeSubscription({
                    code: selectedPlan.code,
                    addOns: getAddOns(selectedAddOnValues, { addOns: selectedPlan.addOns || [] }),
                    ott: token,
                })}
                onSubscribed={() => connectStore.getCurrentPlan()}
                title={selectedPlan ? `Purchase transactions for ${selectedPlan.name}` : ''}
                productLabel=''
                showBasePrice={false}
                showPromo={false}
                payLabel='PURCHASE'
                onSuccess={() => {
                    closeCheckout();
                    onClose();
                    if (onPaymentSuccess) {
                        // remaining is the pre-purchase balance (usage prop); the store hasn't
                        // refetched the live balance yet at this point, hence "approximately".
                        const newRemaining = remaining + quantity;
                        onPaymentSuccess(
                            `${quantity.toLocaleString()} transactions added!`,
                            `Your account has been topped up. You now have approximately ${newRemaining.toLocaleString()} transactions remaining for this period.`
                        );
                    }
                }}
                addOnsOnly
            />
        </React.Fragment>
    );
}

PurchaseTransactionsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    usage: PropTypes.shape({
        used: PropTypes.number,
        total: PropTypes.number,
    }).isRequired,
    onPaymentSuccess: PropTypes.func,
};

const ObservedPurchaseTransactionsDialog = observer(PurchaseTransactionsDialog);
export default ObservedPurchaseTransactionsDialog;
